// Shared progressive enhancement for server-rendered reading catalogs.
const root = document.querySelector('[data-reading-catalog]');
if (root && window.fetch && window.AbortController) {
    const isBook = root.dataset.catalogKind === 'book';
    const noun = isBook ? 'کتاب' : 'مقاله';
    const T = text => isBook ? text.replaceAll('مقاله‌ای', 'کتابی').replaceAll('مقالهٔ', 'کتاب').replaceAll('مقاله', noun) : text;
    const authorKey = isBook ? 'Author' : 'AuthorId';
    const stateKey = isBook ? 'bookCatalog' : 'articleCatalog';
    const form = root.querySelector('[data-catalog-form]');
    const results = root.querySelector('[data-catalog-results]');
    const dialog = root.querySelector('[data-filter-dialog]');
    const status = root.querySelector('[data-catalog-status]');
    const error = root.querySelector('[data-request-error]');
    const loading = root.querySelector('[data-catalog-loading]');
    const chips = root.querySelector('[data-active-filters]');
    const base = new URL(root.dataset.baseUrl, location.href);
    const keys = ['Q', 'TopicId', 'TypeId', 'TagId', authorKey, 'Access', 'RatingMode', 'RatingMin', 'RatingMax', 'Sort', 'Tag'];
    const advancedKeys = ['TypeId', 'TagId', authorKey, 'RatingMode', 'RatingMin', 'RatingMax', 'Tag'];
    const defaults = { Access: 'all', RatingMode: 'any', Sort: root.dataset.defaultSort || 'latest' };
    const control = name => form.elements.namedItem(name);
    const read = () => Object.fromEntries(keys.map(key => [key, control(key)?.value.trim() || defaults[key] || '']));
    const normalize = values => {
        const next = { ...values };
        if (next.TagId) next.Tag = '';
        if (next.RatingMode === 'any' || next.RatingMode === 'below') next.RatingMin = '';
        if (next.RatingMode === 'any' || next.RatingMode === 'above') next.RatingMax = '';
        return next;
    };
    const urlFor = (values, page = 1) => {
        const url = new URL(base);
        const clean = normalize(values);
        for (const key of keys) if (clean[key] && clean[key] !== defaults[key]) url.searchParams.set(key, clean[key]);
        if (page > 1) url.searchParams.set('Page', String(page));
        return url;
    };
    const localPath = url => url.pathname + url.search;
    const currentBatch = () => results.querySelector('[data-catalog-batch]');
    let committed = normalize(read());
    let firstPage = Number(currentBatch().dataset.page);
    let lastPage = firstPage;
    let requestId = 0, controller, observer, busy = false, failed = false, retry;
    let debounce, scrollTimer, dialogBefore, dialogApplied = false, searchHistoryStarted = false;
    let restoring = false, restoreId = 0;
    const initialHistory = history.state?.[stateKey];

    function snapshot() {
        return { key: location.pathname + location.search, firstPage, lastPage, y: window.scrollY };
    }
    function remember() {
        history.replaceState({ ...history.state, [stateKey]: snapshot() }, '', location.href);
    }
    function fill(values, names = keys) {
        for (const key of names) if (control(key)) control(key).value = values[key] || defaults[key] || '';
        syncRating();
    }
    function syncRating() {
        const mode = control('RatingMode').value;
        const min = control('RatingMin'), max = control('RatingMax');
        min.disabled = mode !== 'range' && mode !== 'above';
        max.disabled = mode !== 'range' && mode !== 'below';
        min.required = !min.disabled;
        max.required = !max.disabled;
        root.querySelector('[data-rating-min]').hidden = min.disabled;
        root.querySelector('[data-rating-max]').hidden = max.disabled;
        max.setCustomValidity(mode === 'range' && min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)
            ? 'حداکثر ریتینگ باید از حداقل کمتر نباشد.' : '');
        root.querySelector('[data-rating-help]').textContent = mode === 'below'
            ? T('مقاله‌های مناسب ریتینگ‌های پایین‌تر از این عدد و همهٔ سطح‌ها.')
            : mode === 'above' ? T('مقاله‌های مناسب ریتینگ‌های بالاتر از این عدد و همهٔ سطح‌ها.')
            : T('مقاله‌های مناسب همهٔ سطح‌ها هم در نتایج می‌آیند.');
    }
    function showDialog() {
        if (dialog.open) return;
        clearTimeout(debounce);
        dialogBefore = read();
        dialogApplied = false;
        document.documentElement.classList.add('article-dialog-open');
        if (dialog.showModal) dialog.showModal(); else dialog.setAttribute('open', '');
    }
    function closeDialog(applied = false) {
        if (!dialog.open) return;
        dialogApplied = applied;
        // Restore drafts synchronously: the native close event is queued.
        if (!applied && dialogBefore) fill(dialogBefore, advancedKeys);
        dialogBefore = null;
        document.documentElement.classList.remove('article-dialog-open');
        if (dialog.close) dialog.close(); else {
            dialog.removeAttribute('open');
            onDialogClose();
        }
    }
    function onDialogClose() {
        if (dialog.open) return;
        if (!dialogApplied && dialogBefore) fill(dialogBefore, advancedKeys);
        document.documentElement.classList.remove('article-dialog-open');
        dialogBefore = null;
    }
    function labelFor(key, value) {
        const field = control(key);
        return field?.tagName === 'SELECT' ? [...field.options].find(option => option.value === value)?.textContent || value : value;
    }
    function renderChips() {
        chips.replaceChildren();
        const add = (key, label) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'article-filter-chip';
            button.dataset.removeFilter = key;
            button.setAttribute('aria-label', 'حذف فیلتر ' + label);
            const text = document.createElement('span'); text.textContent = label;
            const cross = document.createElement('span'); cross.textContent = '×'; cross.setAttribute('aria-hidden', 'true');
            button.append(text, cross); chips.append(button);
        };
        const prefixes = { Q: 'جست‌وجو: ', TopicId: 'موضوع: ', TypeId: 'نوع: ', TagId: 'برچسب: ', [authorKey]: 'نویسنده: ', Tag: 'برچسب: ', Access: '' };
        for (const [key, prefix] of Object.entries(prefixes))
            if (committed[key] && committed[key] !== defaults[key]) add(key, prefix + labelFor(key, committed[key]));
        const n = value => Number(value).toLocaleString('fa-IR');
        if (committed.RatingMode !== 'any') {
            const label = committed.RatingMode === 'range' ? 'ریتینگ ' + n(committed.RatingMin) + ' تا ' + n(committed.RatingMax)
                : committed.RatingMode === 'below' ? 'ریتینگ زیر ' + n(committed.RatingMax) : 'ریتینگ بالای ' + n(committed.RatingMin);
            add('RatingMode', label);
        }
        const hasChips = chips.childElementCount > 0;
        if (hasChips) {
            const clear = document.createElement('button');
            clear.type = 'button'; clear.className = 'article-text-button'; clear.dataset.clearFilters = '';
            clear.textContent = 'پاک‌کردن همه'; chips.append(clear);
        }
        chips.hidden = !hasChips;
        const count = ['TypeId', 'TagId', authorKey, 'Tag'].filter(key => committed[key]).length + (committed.RatingMode !== 'any' ? 1 : 0);
        const badge = root.querySelector('[data-advanced-count]');
        badge.textContent = count.toLocaleString('fa-IR'); badge.hidden = count === 0;
        root.querySelector('[data-open-filters]').classList.toggle('has-filters', count > 0);
    }
    function setBusy(value, append) {
        busy = value;
        results.setAttribute('aria-busy', String(value));
        loading.hidden = !value;
        root.classList.toggle('is-filtering', value && !append);
        const more = results.querySelector('[data-catalog-more]');
        if (more) {
            more.setAttribute('aria-disabled', String(value));
            more.textContent = value && append ? 'در حال دریافت…' : T('نمایش مقاله‌های بیشتر ↓');
        }
    }
    function observe() {
        observer?.disconnect();
        const sentinel = results.querySelector('[data-catalog-sentinel]');
        if (sentinel && results.querySelector('[data-catalog-more]') && !busy && !failed && !restoring) observer?.observe(sentinel);
    }
    function checkedNext(batch, page) {
        if (!batch.dataset.nextUrl) return null;
        const next = new URL(batch.dataset.nextUrl, base);
        if (next.origin !== base.origin || next.pathname !== base.pathname || Number(next.searchParams.get('Page')) <= page)
            throw Error('پاسخ فهرست معتبر نیست.');
        return next;
    }
    async function load(url, { append = false, values = committed, historyMode = 'push' } = {}) {
        if (append && busy) return false;
        const target = new URL(url, base);
        if (target.origin !== base.origin || target.pathname !== base.pathname) return false;
        observer?.disconnect();
        controller?.abort();
        const id = ++requestId;
        controller = new AbortController();
        const aborter = controller;
        const signal = aborter.signal;
        let timedOut = false;
        const timeout = setTimeout(() => { timedOut = true; aborter.abort(); }, 15000);
        error.hidden = true;
        setBusy(true, append);
        status.textContent = append ? T('مقاله‌های بیشتر در حال دریافت هستند.') : T('در حال پیدا کردن مقاله‌ها…');
        const options = { append, values: { ...values }, historyMode };
        try {
            const requestUrl = new URL(target); requestUrl.searchParams.set('handler', 'Results');
            const response = await fetch(requestUrl, { credentials: 'same-origin', cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal });
            if (!response.ok) {
                const payload = await response.json().catch(() => null);
                throw Error(payload?.errors?.filter(Boolean).join(' ') || payload?.message || T('دریافت مقاله‌ها انجام نشد؛ دوباره تلاش کن.'));
            }
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            if (id !== requestId) return false;
            const batch = doc.querySelector('[data-catalog-batch]');
            const page = Number(batch?.dataset.page), total = Number(batch?.dataset.total);
            if (!batch?.querySelector('[data-catalog-cards]') || !Number.isInteger(page) || page < 1 || !Number.isInteger(total) || total < 0)
                throw Error('پاسخ فهرست معتبر نیست؛ دوباره تلاش کن.');
            const nextUrl = checkedNext(batch, page);
            const previous = snapshot();
            if (append) {
                const current = currentBatch();
                const grid = current.querySelector('[data-catalog-cards]');
                const ids = new Set([...grid.querySelectorAll('[data-catalog-id]')].map(card => card.dataset.catalogId));
                const fragment = document.createDocumentFragment();
                let added = 0;
                if (page > lastPage) for (const card of batch.querySelectorAll('[data-catalog-id]'))
                    if (!ids.has(card.dataset.catalogId)) { ids.add(card.dataset.catalogId); fragment.append(card); added++; }
                grid.append(fragment);
                const more = current.querySelector('[data-catalog-more]');
                if (nextUrl && added) more.href = localPath(nextUrl); else more?.remove();
                lastPage = Math.max(lastPage, page);
                current.dataset.page = String(lastPage); current.dataset.total = String(total);
                current.dataset.nextUrl = nextUrl && added ? localPath(nextUrl) : '';
                status.textContent = added ? added.toLocaleString('fa-IR') + T(' مقالهٔ دیگر اضافه شد.') : T('همهٔ مقاله‌ها نمایش داده شد.');
            } else {
                results.replaceChildren(document.importNode(batch, true));
                committed = normalize({ ...values });
                firstPage = lastPage = page;
                const clean = urlFor(committed, firstPage);
                if (historyMode !== 'none') {
                    if (historyMode === 'push' && localPath(clean) !== location.pathname + location.search) {
                        history.replaceState({ ...history.state, [stateKey]: previous }, '', location.href);
                        history.pushState({ ...history.state, [stateKey]: { key: localPath(clean), firstPage, lastPage, y: window.scrollY } }, '', clean);
                    } else history.replaceState({ ...history.state, [stateKey]: snapshot() }, '', clean);
                }
                root.querySelector('.article-server-errors')?.replaceChildren();
                renderChips();
                status.textContent = total ? total.toLocaleString('fa-IR') + T(' مقاله پیدا شد.') : T('مقاله‌ای با این انتخاب‌ها پیدا نشد.');
            }
            const shown = results.querySelectorAll('[data-catalog-id]').length;
            root.querySelector('[data-catalog-total]').textContent = total.toLocaleString('fa-IR') + ' عنوان';
            currentBatch().querySelector('[data-load-status]').textContent = shown && !results.querySelector('[data-catalog-more]')
                ? 'همهٔ ' + shown.toLocaleString('fa-IR') + T(' مقاله نمایش داده شد.') : '';
            failed = false; retry = null;
            remember();
            return true;
        } catch (cause) {
            if (id !== requestId || (signal.aborted && !timedOut)) return false;
            failed = true; retry = { url: target, options };
            status.textContent = '';
            error.querySelector('[data-error-message]').textContent = timedOut ? T('دریافت مقاله‌ها طول کشید؛ دوباره تلاش کن.') : cause.message;
            error.hidden = false;
            return false;
        } finally {
            clearTimeout(timeout);
            if (id === requestId) { setBusy(false, append); observe(); }
        }
    }
    function apply(historyMode = 'push') {
        clearTimeout(debounce);
        syncRating();
        if (!form.reportValidity()) return;
        restoreId++; restoring = false;
        const values = normalize(read());
        closeDialog(true);
        load(urlFor(values), { values, historyMode });
    }
    function clearFilters() {
        fill({ ...defaults });
        searchHistoryStarted = false;
        apply();
    }
    async function restore(state, alreadyLoaded = false) {
        const token = ++restoreId;
        restoring = true;
        observer?.disconnect();
        clearTimeout(debounce);
        const values = Object.fromEntries(keys.map(key => [key, new URL(location.href).searchParams.get(key) || defaults[key] || '']));
        fill(values);
        const start = Math.max(1, Number(new URL(location.href).searchParams.get('Page')) || 1);
        let ok = alreadyLoaded || await load(urlFor(values, start), { values, historyMode: 'none' });
        const through = Math.max(start, Math.min(100000, Number(state?.lastPage) || start));
        while (ok && token === restoreId && lastPage < through) {
            const more = results.querySelector('[data-catalog-more]');
            if (!more) break;
            ok = await load(more.href, { append: true, historyMode: 'none' });
        }
        if (token !== restoreId) return;
        restoring = false;
        if (ok) requestAnimationFrame(() => window.scrollTo({ top: Math.max(0, Number(state?.y) || 0), behavior: 'instant' }));
        observe();
    }

    if ('IntersectionObserver' in window) observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !failed && !busy && !restoring) {
            const more = results.querySelector('[data-catalog-more]');
            if (more) load(more.href, { append: true, historyMode: 'none' });
        }
    }, { rootMargin: '0px 0px 300px 0px' });

    form.addEventListener('submit', event => { event.preventDefault(); apply(); });
    form.addEventListener('invalid', event => { if (dialog.contains(event.target)) showDialog(); }, true);
    root.addEventListener('input', event => {
        if (event.target.name === 'Q') {
            clearTimeout(debounce);
            debounce = setTimeout(() => { apply(searchHistoryStarted ? 'replace' : 'push'); searchHistoryStarted = true; }, 380);
        }
        if (event.target.name === 'RatingMin' || event.target.name === 'RatingMax') syncRating();
    });
    control('Q').addEventListener('focus', () => { searchHistoryStarted = false; });
    root.addEventListener('change', event => {
        if (event.target.name === 'RatingMode') syncRating();
        else if (event.target.name === 'TagId' && control('Tag')) control('Tag').value = '';
        else if (['TopicId', 'Access', 'Sort'].includes(event.target.name)) { searchHistoryStarted = false; apply(); }
    });
    root.addEventListener('click', event => {
        const target = event.target.closest('button,a');
        if (!target) return;
        if (target.matches('[data-open-filters]')) showDialog();
        else if (target.matches('[data-close-filters]')) closeDialog();
        else if (target.matches('[data-reset-advanced]')) fill({ ...defaults }, advancedKeys);
        else if (target.matches('[data-retry]') && retry) load(retry.url, retry.options);
        else if (target.matches('[data-remove-filter]')) {
            fill(committed);
            const key = target.dataset.removeFilter;
            control(key).value = defaults[key] || '';
            if (key === 'RatingMode') { control('RatingMin').value = ''; control('RatingMax').value = ''; }
            apply();
        } else if (target.matches('[data-clear-filters],[data-catalog-more],[data-catalog-previous]')) {
            if (event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            if (target.matches('[data-clear-filters]')) clearFilters();
            else if (target.matches('[data-catalog-more]')) load(target.href, { append: true, historyMode: 'none' });
            else load(target.href, { values: committed });
        } else if (target.matches('[data-catalog-id]')) remember();
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); closeDialog(); });
    dialog.addEventListener('close', onDialogClose);
    dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDialog();
    });
    addEventListener('popstate', event => { closeDialog(); restore(event.state?.[stateKey]); });
    addEventListener('scroll', () => { clearTimeout(scrollTimer); scrollTimer = setTimeout(remember, 160); }, { passive: true });
    addEventListener('pagehide', () => {
        remember(); clearTimeout(debounce); clearTimeout(scrollTimer);
        requestId++; controller?.abort(); observer?.disconnect(); setBusy(false, false);
    });
    addEventListener('pageshow', event => { if (event.persisted) observe(); });
    function coverFallback(img) {
        img.hidden = true;
        const fallback = img.parentElement.querySelector('.book-cover-placeholder');
        if (fallback) fallback.hidden = false;
    }
    root.addEventListener('error', event => {
        if (event.target.matches('.article-card-cover img,.book-card-cover img')) coverFallback(event.target);
    }, true);
    root.querySelectorAll('.library-card-cover img').forEach(img => {
        if (img.complete && img.naturalWidth === 0) coverFallback(img);
    });
    root.dataset.enhanced = 'true';
    root.querySelector('[data-open-filters]').hidden = false;
    syncRating(); renderChips();
    const clean = urlFor(committed, firstPage);
    history.replaceState({ ...history.state, [stateKey]: initialHistory || snapshot() }, '', clean);
    if (initialHistory?.key === localPath(clean) && (initialHistory.lastPage > lastPage || initialHistory.y > 0)) restore(initialHistory, true);
    else observe();
}
